import { ChevronDown, Plus, Tag as TagIcon } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { type SalesLeadTag, tagColor } from '@/models/salesLeadTag'

interface TagSelectProps {
  tags: SalesLeadTag[]
  selectedIds: string[]
  onToggle: (tagId: string) => void
  // When provided, a "Create '<text>'" row appears first while typing a new tag.
  // Omit it (e.g. in the filter) to disable creating tags.
  onCreate?: (name: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function TagChip({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', tagColor(name), className)}>{name}</span>
  )
}

export function TagSelect({
  tags,
  selectedIds,
  onToggle,
  onCreate,
  placeholder = 'Tags',
  className,
  disabled,
}: TagSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selectedSet = new Set(selectedIds)
  const selectedTags = tags.filter((t) => selectedSet.has(t.id))

  const q = query.trim()
  const filtered = q ? tags.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())) : tags
  const exactMatch = tags.some((t) => t.name.toLowerCase() === q.toLowerCase())
  const showCreate = !!onCreate && q.length > 0 && !exactMatch

  function handleCreate() {
    onCreate?.(q)
    setQuery('')
  }

  return (
    // `modal` so the list wheel-scrolls when this popover is opened inside a
    // Dialog: without it the Dialog's scroll-lock (react-remove-scroll) blocks
    // wheel events on the portaled popover content (only scrollbar-drag works).
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outlineThin"
          disabled={disabled}
          className={cn('h-auto min-h-10 w-full justify-start gap-2 py-1.5 font-normal', className)}
        >
          <TagIcon className="text-muted-foreground size-4 shrink-0" />
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selectedTags.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedTags.map((t) => <TagChip key={t.id} name={t.name} />)
            )}
          </span>
          <ChevronDown className="text-muted-foreground size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={onCreate ? 'Search or create…' : 'Search tags…'}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {showCreate && (
              <CommandGroup>
                <CommandItem value={`__create__${q}`} onSelect={handleCreate} className="cursor-pointer">
                  <Plus className="size-4 shrink-0" />
                  <span className="text-muted-foreground">Create</span>
                  <TagChip name={q} />
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((tag) => (
                  <CommandItem
                    key={tag.id}
                    value={tag.id}
                    onSelect={() => onToggle(tag.id)}
                    className="cursor-pointer"
                  >
                    <Checkbox checked={selectedSet.has(tag.id)} className="pointer-events-none" />
                    <TagChip name={tag.name} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filtered.length === 0 && !showCreate && (
              <div className="text-muted-foreground py-6 text-center text-sm">
                {onCreate ? 'Type to create a tag' : 'No tags found'}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
